import { useEffect, useState } from 'react'

/**
 * Loads an image and keys out its near-black background, returning a transparent
 * data URL. Falls back to the original source if the canvas cannot be read.
 */
export function useTransparentImage(src: string, cutoff = 22, fullAt = 48): string {
  const [processed, setProcessed] = useState(src)

  useEffect(() => {
    let cancelled = false
    setProcessed(src)

    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.src = src

    image.onload = () => {
      if (cancelled) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = image.naturalWidth
        canvas.height = image.naturalHeight
        const context = canvas.getContext('2d')
        if (!context) return

        context.drawImage(image, 0, 0)
        const data = context.getImageData(0, 0, canvas.width, canvas.height)
        const pixels = data.data

        for (let i = 0; i < pixels.length; i += 4) {
          const brightest = Math.max(pixels[i], pixels[i + 1], pixels[i + 2])
          if (brightest <= cutoff) {
            pixels[i + 3] = 0
          } else if (brightest < fullAt) {
            pixels[i + 3] = Math.round(((brightest - cutoff) / (fullAt - cutoff)) * 255)
          }
        }

        context.putImageData(data, 0, 0)
        const url = canvas.toDataURL('image/png')
        if (!cancelled) setProcessed(url)
      } catch {
        /* tainted canvas — keep the original image */
      }
    }

    return () => {
      cancelled = true
    }
  }, [src, cutoff, fullAt])

  return processed
}
